import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';
import { useUIStore } from '../store/ui';
import Loader from '../components/Loader';

interface Auction {
  id: string;
  userId: number;
  title: string;
  description: string;
  images: string[];
  startPrice: number;
  currentPrice: number;
  minStep: number;
  currency: string;
  endsAt: string;
  bidsCount: number;
  user: {
    firstName: string;
    rating: number;
    telegramId: number;
  };
}

interface Bid {
  id: string;
  amount: number;
  createdAt: string;
  first_name: string;
}

export default function AuctionDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { showToast } = useUIStore();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [timeLeft, setTimeLeft] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadAuction();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (auction) {
      updateTimeLeft();
      timerRef.current = setInterval(updateTimeLeft, 1000);
      setBidAmount(String(auction.currentPrice + auction.minStep));
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [auction?.endsAt]);

  const loadAuction = async () => {
    if (!id) return;
    try {
      const [auctionRes, bidsRes] = await Promise.all([
        apiService.getAuction(id),
        apiService.getAuctionBids(id)
      ]);
      setAuction(auctionRes.data.data);
      setBids(bidsRes.data.data || []);
    } catch (error) {
      console.error('Error loading auction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTimeLeft = () => {
    if (!auction) return;
    const end = new Date(auction.endsAt);
    const now = new Date();
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) {
      setTimeLeft('Завершён');
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) {
      setTimeLeft(`${days}д ${hours}ч ${minutes}м`);
    } else if (hours > 0) {
      setTimeLeft(`${hours}ч ${minutes}м ${seconds}с`);
    } else {
      setTimeLeft(`${minutes}м ${seconds}с`);
    }
  };

  const handlePlaceBid = async () => {
    if (!auction || !user || !bidAmount) return;

    const amount = parseFloat(bidAmount);
    const minBid = auction.currentPrice + auction.minStep;

    if (amount < minBid) {
      showToast(`Минимальная ставка: ${minBid}`, 'error');
      return;
    }

    try {
      await apiService.placeBid(auction.id, {
        userId: user.id,
        amount
      });

      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      showToast('Ставка принята!', 'success');
      loadAuction();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Ошибка при размещении ставки';
      showToast(message, 'error');
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    const symbols: Record<string, string> = {
      RUB: '₽',
      KZT: '₸',
      BYN: 'Br'
    };
    return `${amount.toLocaleString('ru-RU')} ${symbols[currency] || currency}`;
  };

  if (isLoading) {
    return <Loader className="py-12" />;
  }

  if (!auction) {
    return (
      <div className="text-center py-8">
        <p className="text-tg-theme-hint">Аукцион не найден</p>
      </div>
    );
  }

  const isEnded = new Date(auction.endsAt) <= new Date();
  const isOwner = user?.telegramId === auction.user.telegramId;
  const minBid = auction.currentPrice + auction.minStep;

  return (
    <div className="space-y-4 -mx-4">
      {/* Image */}
      <div className="aspect-video bg-tg-theme-secondary-bg">
        {auction.images.length > 0 ? (
          <img
            src={auction.images[0]}
            alt={auction.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl text-tg-theme-hint">
            🔨
          </div>
        )}
      </div>

      <div className="px-4 space-y-4">
        {/* Timer */}
        <div className={`text-center py-3 rounded-lg ${isEnded ? 'bg-gray-100' : 'bg-red-50'}`}>
          <p className="text-sm text-tg-theme-hint">
            {isEnded ? 'Аукцион завершён' : 'До окончания'}
          </p>
          <p className={`text-2xl font-bold ${isEnded ? 'text-gray-500' : 'text-red-500'}`}>
            {timeLeft}
          </p>
        </div>

        {/* Title & Price */}
        <h1 className="text-xl font-bold">{auction.title}</h1>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-tg-theme-hint">Текущая ставка</p>
            <p className="text-2xl font-bold text-tg-theme-button">
              {formatPrice(auction.currentPrice, auction.currency)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-tg-theme-hint">Ставок</p>
            <p className="text-2xl font-bold">{auction.bidsCount}</p>
          </div>
        </div>

        {/* Place Bid */}
        {!isEnded && !isOwner && (
          <div className="card">
            <p className="text-sm mb-2">
              Мин. ставка: {formatPrice(minBid, auction.currency)}
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                className="input flex-1"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                min={minBid}
                step={auction.minStep}
              />
              <button onClick={handlePlaceBid} className="btn-primary">
                Ставка
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="card">
          <h3 className="font-semibold mb-2">Описание</h3>
          <p className="text-sm whitespace-pre-wrap">{auction.description}</p>
        </div>

        {/* Bids History */}
        <div className="card">
          <h3 className="font-semibold mb-3">История ставок</h3>
          {bids.length === 0 ? (
            <p className="text-sm text-tg-theme-hint">Ставок пока нет</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {bids.map((bid, index) => (
                <div
                  key={bid.id}
                  className={`flex items-center justify-between py-2 ${
                    index === 0 ? 'text-tg-theme-button font-semibold' : ''
                  }`}
                >
                  <span>{bid.first_name}</span>
                  <span>{formatPrice(bid.amount, auction.currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Seller */}
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-tg-theme-button flex items-center justify-center text-white text-xl">
              {auction.user.firstName[0]}
            </div>
            <div>
              <p className="font-semibold">{auction.user.firstName}</p>
              <p className="text-sm text-tg-theme-hint">
                ⭐️ {auction.user.rating.toFixed(1)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
